import deepmerge from 'deepmerge'
import { useState } from 'react'

import { ABO_LABEL, PRODUCT_STATUS_AVAILABLE } from 'shared/consts'
import { isVariantAttributes } from 'shared/experiments/utils/featureFlags'
import { normalizeUrl } from 'shared/utils/url'
import { isSaeOffer } from 'views/components/organisms/FilteredProductList/SearchProductListItem/helpers'
import { promotionTypeMapping } from 'views/consts/promotions'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import {
  isProductOutOfStock,
  isSingleVariantAttributeSERP,
} from 'views/utils/productUtils'

const sortVariants = (a, b) => {
  const amountA = a.best_offer?.price?.amount
  const amountB = b.best_offer?.price?.amount

  if (!amountA && !amountB) {
    return 0
  }

  if (!amountA) {
    return 1
  }

  if (!amountB) {
    return -1
  }

  return amountA - amountB
}

const getCheapestVariant = variants => {
  const availableVariants = variants.filter(variant => {
    return (
      variant.stockStatus &&
      variant.stockStatus.status === PRODUCT_STATUS_AVAILABLE
    )
  })

  if (!availableVariants || availableVariants.length < 1) {
    return undefined
  }

  const sortedVariants = availableVariants.sort(sortVariants)

  return sortedVariants[0]
}

const getVariantDiscount = ({ stockStatus, discount } = {}) => {
  return isProductOutOfStock(stockStatus) ? null : discount
}

/**
 * @template {object} VariantState
 * @template {object} Variant
 * @typedef {[variantState: VariantState, setVariantState: (variant: Variant) => void]} UseVariantState<VariantState,Variant>
 */

/**
 * @template {object} VariantState
 * @template {object} Variant
 * @param {object} product
 * @returns {UseVariantState<VariantState,Variant>}
 */
const useVariantState = product => {
  const { publicRuntimeConfig } = useShopAndRuntimeConfig()

  const {
    flags: { prescriptionMedicine },
  } = product

  const { pageProperties, publicConfig } = publicRuntimeConfig

  const { experiments } = pageProperties

  const { tenant } = publicConfig

  const variantAttributesEnabled = isVariantAttributes(experiments)

  const getInitialState = () => {
    const getPriceInfo = currentVariant => {
      const usedVariant =
        (variantAttributesEnabled &&
          !isSingleVariantAttributeSERP(product.variants) &&
          product.variants?.length > 1 &&
          getCheapestVariant(variants)) ||
        currentVariant

      return {
        prescriptionMedicine,
        prices: usedVariant.prices,
        productId: product.id,
        stockStatus: usedVariant.stockStatus,
        unitPricing: usedVariant.unitPricing,
      }
    }

    const {
      best_offer: bestOffer,
      canonicalURL,
      deeplink,
      descriptionShort,
      displayCode,
      ean,
      icd10,
      id,
      imageUrl,
      isbn,
      labels,
      manufacturer,
      multiplierRP,
      name,
      nowProductGroup,
      objectID,
      ods_partners,
      offers_count: offerCount = 0,
      pharmaForm,
      promotion,
      promotionProduct,
      pzn,
      seller_count: sellerCount = 0,
      unit,
      unitQuantityString,
      upid,
      variants = [],
    } = product

    const offerTypeSae = isSaeOffer({
      bestOffer,
      experiments,
      tenant,
    })

    const currentVariant =
      variants.find(variant => variant.id === id) || product

    const labelsWithoutAboLabel = labels.filter(
      labelName => labelName !== ABO_LABEL
    )

    const offers = {
      count: offerCount,
      currentOfferId: bestOffer?.id,
    }

    const packageDetails = {
      id,
      manufacturer,
      pharmaForm,
      unit,
      unitQuantityString,
    }

    const sellerBoxParams = {
      offerType: bestOffer?.type,
      sellerId: bestOffer?.seller?.id,
      sellerName: bestOffer?.seller?.name,
      productId: upid,
    }

    return {
      bestOffer,
      canonicalURL: normalizeUrl(deeplink) || normalizeUrl(canonicalURL),
      currentVariantId: id,
      descriptionShort,
      discount: getVariantDiscount(currentVariant),
      displayCode,
      ean,
      icd10,
      imageUrl,
      isbn,
      labelsWithoutAboLabel,
      multiplierRP: multiplierRP || bestOffer?.redpoints?.multiplier,
      name,
      nowProductGroup,
      objectID,
      ods_partners,
      offers,
      offerTypeSae,
      packageDetails,
      priceInfo: getPriceInfo(currentVariant),
      promotion: promotion || promotionTypeMapping[bestOffer?.promotion?.type],
      promotionProduct,
      pzn,
      sellerBoxParams,
      sellerCount,
      type: bestOffer?.type,
      upid,
    }
  }

  const [variantState, setState] = useState(getInitialState)

  const extractVariantProps = variant => {
    const {
      best_offer: bestOffer,
      canonicalURL,
      descriptionShort,
      discount,
      displayCode,
      ean,
      id,
      imageUrl,
      isbn,
      labels,
      multiplierRP,
      nowProductGroup,
      objectID,
      offers_count: offerCount = 0,
      pharmaForm,
      prices,
      productName,
      promotion,
      promotionProduct,
      pzn,
      seller_count: sellerCount = 0,
      stockStatus,
      unit,
      unitPricing,
      unitQuantityString,
      upid,
    } = variant

    const labelsWithoutAboLabel =
      labels?.filter(labelName => labelName !== ABO_LABEL) || []

    const offers = {
      count: offerCount,
      currentOfferId: bestOffer?.id,
    }

    const packageDetails = {
      id,
      pharmaForm,
      unit,
      unitQuantityString,
    }

    const priceInfo = {
      prices,
      productId: id,
      stockStatus,
      unitPricing,
    }

    const sellerBoxParams = {
      offerType: bestOffer?.type,
      sellerId: bestOffer?.seller?.id,
      sellerName: bestOffer?.seller?.name,
    }

    return {
      bestOffer,
      canonicalURL,
      currentVariantId: id,
      descriptionShort,
      displayCode,
      discount: isProductOutOfStock(stockStatus) ? null : discount,
      ean,
      imageUrl,
      isbn,
      labelsWithoutAboLabel,
      multiplierRP: multiplierRP || bestOffer?.redpoints?.multiplier,
      name: productName,
      nowProductGroup,
      objectID,
      offers,
      packageDetails,
      priceInfo,
      promotion: promotion || promotionTypeMapping[bestOffer?.promotion?.type],
      promotionProduct,
      pzn,
      sellerBoxParams,
      sellerCount,
      type: bestOffer?.type,
      productId: upid,
    }
  }

  const setVariantState = variant => {
    const newState = extractVariantProps(variant)
    const { labelsWithoutAboLabel, multiplierRP } = newState

    const offerTypeSae = isSaeOffer({
      bestOffer: variant?.best_offer,
      experiments,
      tenant,
    })

    // Merge the props except of prices:
    // priceInfo contains also stock status
    setState(prevState => ({
      ...deepmerge(prevState, newState),
      labelsWithoutAboLabel,
      multiplierRP,
      offerTypeSae,
      priceInfo: {
        ...prevState.priceInfo,
        prices: newState.priceInfo.prices,
        stockStatus: newState.priceInfo.stockStatus,
        unitPricing: newState.priceInfo.unitPricing,
      },
      offers: {
        count: newState.offers.count,
        currentOfferId: newState.offers.currentOfferId,
      },
    }))
  }

  return [variantState, setVariantState]
}

export default useVariantState
